/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateHomeTaskSchema,
  HOME_TASK_CATEGORIES,
  type CreateHomeTaskInput,
} from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { useCreateHomeTask } from '../../lib/queries/home.js';

interface HomeTaskFormProps {
  readonly clientId: string;
  readonly onDone: () => void;
}

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** "Assign Home Task" form (Figma). Validated by shared `CreateHomeTaskSchema`. */
export function HomeTaskForm({ clientId, onDone }: HomeTaskFormProps): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateHomeTask(clientId);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateHomeTaskInput>({
    resolver: zodResolver(CreateHomeTaskSchema),
    defaultValues: { clientId },
  });

  const submit = useCallback(
    async (values: CreateHomeTaskInput): Promise<void> => {
      await create.mutateAsync({
        clientId,
        title: values.title,
        category: values.category,
        ...(values.instructions !== undefined && values.instructions !== ''
          ? { instructions: values.instructions }
          : {}),
      });
      onDone();
    },
    [create, clientId, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <input type="hidden" {...register('clientId')} />
      <FormField
        id="task-title"
        type="text"
        label={t('form.homeTask.title')}
        placeholder={t('form.homeTask.titleHint')}
        autoComplete="off"
        error={errors.title?.message}
        registration={register('title')}
      />
      <div>
        <label htmlFor="task-category" className={LABEL_CLASS}>
          {t('form.homeTask.category')}
        </label>
        <select
          id="task-category"
          className={SELECT_CLASS}
          defaultValue=""
          aria-invalid={errors.category !== undefined}
          {...register('category')}
        >
          <option value="" disabled>
            {t('form.homeTask.selectCategory')}
          </option>
          {HOME_TASK_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`homeCategory.${c}`)}
            </option>
          ))}
        </select>
        {errors.category !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.category.message}
          </p>
        )}
      </div>
      <FormField
        id="task-instructions"
        type="text"
        label={t('form.homeTask.instructions')}
        placeholder={t('form.homeTask.instructionsHint')}
        autoComplete="off"
        error={errors.instructions?.message}
        registration={register('instructions')}
      />

      {create.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || create.isPending}>
          {t('form.homeTask.submit')}
        </Button>
      </div>
    </form>
  );
}
